import { useEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';
import { ContainerTreeSizeProvider } from '../ContainerTreeSizeProvider';
import { TreeEditorContent } from './TreeEditorContent';
import { type TreeEditorProps } from './types';
import { useTreeEditorRows } from './useTreeEditorRows';
import styles from './TreeEditor.module.css';

export function WindowTreeEditor<Attributes>(props: TreeEditorProps<Attributes>) {
    const { 'data-testid': dataTestId = 'TreeEditor', disableDragAndDrop = false, settings } = props;
    const flatNodes = useTreeEditorRows(props);
    const visibleNodes = useMemo(
        () => (props.isNodeVisible ? flatNodes.filter(props.isNodeVisible) : flatNodes),
        [flatNodes, props.isNodeVisible]
    );
    const listRef = useRef<HTMLDivElement>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    useEffect(() => {
        const listElement = listRef.current;

        if (!listElement) {
            return;
        }

        const measure = () => {
            setScrollMargin(listElement.getBoundingClientRect().top + window.scrollY);
        };

        measure();

        const observer = new ResizeObserver(measure);

        observer.observe(document.body);

        return () => {
            observer.disconnect();
        };
    }, []);

    const virtualizer = useWindowVirtualizer({
        count: visibleNodes.length,
        estimateSize: () => 44,
        getItemKey: (index) => visibleNodes[index]?.id ?? index,
        overscan: 50,
        scrollMargin,
    });
    const virtualItems = virtualizer.getVirtualItems();
    const items = useMemo(
        () => virtualItems.map((virtualItem) => visibleNodes[virtualItem.index]),
        [virtualItems, visibleNodes]
    );
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start - scrollMargin : 0;
    const paddingBottom =
        virtualItems.length > 0 ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end : 0;

    return (
        <div
            className={cn(styles.containerWindow, settings?.editorStyles)}
            data-testid={dataTestId}
            data-dnd-viewport={!disableDragAndDrop}
        >
            <div
                ref={listRef}
                style={{ width: '100%', paddingTop: `${paddingTop}px`, paddingBottom: `${paddingBottom}px` }}
            >
                <ContainerTreeSizeProvider containerRef={listRef}>
                    <TreeEditorContent {...props} flatNodes={flatNodes} items={items} />
                </ContainerTreeSizeProvider>
            </div>
        </div>
    );
}
